import {
    ChangeEvent,
    forwardRef,
    Fragment,
    useEffect,
    useRef,
    useState,
} from 'react'
import { classNames } from 'utils/dom-helpers'
import { Menu, Transition } from '@headlessui/react'
import { ChevronDownIcon } from '@heroicons/react/solid'
import Translation from 'components/Translation'
import { useAuth } from 'context/AuthContext'
import { useAppContext } from 'context/AppContext'
import useDebounce from 'utils/useDebounceHook'
import { useApiRequest } from 'utils/fetch-helper'
import { useTenantContext } from 'context/TenantContext'
import { useCallback } from 'react'
const defaultHandler = (e: { target: { value: string } }) => { }

export type UniversalSearchProps = {
    selected_idx?: number
    categories?: string[]
    className?: string
    onChange?(e: { target: { value: string } }): void
    onClose?(): void
    scrollUp?(): void
    scrollDown?(): void
    chooseItem?(): void
    displaySuggestions?: boolean
}

export const SEARCH_OPTIONS = [
    {
        id: 0,
        title: 'all',
        value: 'all',
    },
    {
        id: 1,
        title: 'staff',
        value: 'staff',
    },
    // {
    //     id: 2,
    //     title: 'tenants',
    //     value: 'tenants',
    // },
    { id: 2, title: 'clients', value: 'clients' },
]
const SEARCH_TYPE = 'SEARCH_TYPE'
export function UniversalSearch(
    {
        selected_idx = 0,
        categories = [] as string[],
        className,
        onChange = defaultHandler,
        onClose = () => { },
        scrollUp = () => { },
        scrollDown = () => { },
        chooseItem = () => { },
        displaySuggestions = true,
    }: UniversalSearchProps,
    ref: unknown
) {
    const { getApiRequest } = useApiRequest()
    const { translations } = useAppContext()
    const { CreateClientModal, StaffModal } = useAuth()
    const { tenant } = useTenantContext()

    const [is_dropped, setDropdown] = useState<boolean>(false)
    const [keyword, setKeyword] = useState<string>('')
    const debouncedSearchTerm = useDebounce(keyword, 500).trim()
    const [selectedOption, setSelectedOption] = useState(0)
    const [searchResults, setSearchResults] = useState<any>({})
    const search = translations['search']
    const inputRef = useRef<HTMLElement>()

    function clearResults() {
        setDropdown(false)
        onClose()
    }

    function doSearch(e: { target: { value: string } }) {
        setDropdown(true)
        onChange(e)
    }

    const handleOptionSelect = (index: number) => {
        setSelectedOption(index)
        localStorage.setItem(SEARCH_TYPE, index + '')
    }

    useEffect(() => {
        if (!localStorage.getItem(SEARCH_TYPE)) {
            localStorage.setItem(SEARCH_TYPE, '0')
        } else setSelectedOption(localStorage?.getItem(SEARCH_TYPE) as any)
    }, [])

    useEffect(() => {
        if (debouncedSearchTerm.length > 1) {
            const number = localStorage.getItem(SEARCH_TYPE) as string;
            const selectedValue = SEARCH_OPTIONS[+number].value;

            if (selectedValue === "all") {
                Promise.all([
                    getApiRequest(`/v1/clients/search?searchStr=${debouncedSearchTerm}&tenantId=${tenant?.id}&size=50`),
                    getApiRequest(`/v1/staff/search?searchStr=${debouncedSearchTerm}&tenantId=${tenant?.id}&size=50`)
                ]).then(([clients, staff]) => {
                    if (Array.isArray(clients?.content) && Array.isArray(staff?.content)) {
                        clients.content.push(...staff.content);
                    }
                    setSearchResults(clients || {});
                });
            } else {
                getApiRequest(`/v1/${selectedValue}/search?searchStr=${debouncedSearchTerm}&tenantId=${tenant?.id}&size=50`)
                    .then((res: any) => setSearchResults(res));
            }            
        }
    }, [debouncedSearchTerm, selectedOption]);

    const clientHandler = (result: any) => {
        CreateClientModal.setAttributes({
            email: result?.user.email,
            idx: 0
        })
        CreateClientModal.open()
    }
    const tenantHandler = (result: any) => { }
    const staffHandler = (result: any) => {
        const { user } = result
        const mappedData = {
            id: result?.id,
            email: user?.email,
            first_name: user?.firstName,
            last_name: user?.lastName,
            phone: user?.phone,
            image_url: user?.imageUrl,
            role: user?.role,
            hourly_rate: result?.hourlyWage,
            user_id: user?.id,
            overtime_rate: result?.overtimeRate,
            monthly_rate: result?.monthlyWage,
            idx: 0,
            notes: result?.notes,
        }

        StaffModal.setAttributes({ ...mappedData })
        StaffModal.open()
    }

    const onClickHandler = (result: any) => {
        const number = localStorage.getItem(SEARCH_TYPE) as string
        switch (+number) {
            case 0:
                clientHandler(result)
                break
            case 1:
                staffHandler(result)
                break
            case 2:
                clientHandler(result)
                break
            case 3:
                null
                break
            default:
                return null
        }
        setSearchResults({})
        setKeyword('')
    }

    const getSuggestions = () => {
        let output = null

        if (!!debouncedSearchTerm.length && !searchResults?.content?.length)
            output = <div>asdasd</div>
        else if (!!debouncedSearchTerm && searchResults?.content?.length)
            output = (
                <div
                    className="z-10 bg-white border-gray-200 shadow-md rounded-md h-auto absolute w-loose right-auto "
                    style={{ borderWidth: '1px' }}
                >
                    <div>
                        {searchResults?.content?.map(
                            (result: any, index: number) => (
                                <div
                                    className="flex items-center w-full cursor-pointer hover:bg-gray-100 h-12"
                                    key={index}
                                    onClick={() => onClickHandler(result)}
                                >
                                    <div className="px-10">
                                        <p className="text-black font-semibold">
                                            {result?.user?.firstName} &nbsp;
                                            {result?.user?.lastName}
                                        </p>
                                    </div>
                                    <hr />
                                </div>
                            )
                        )}
                    </div>
                </div>
            )
        return output
    }

    function handleInputChange(e: ChangeEvent<HTMLInputElement>) {
        e.preventDefault()
        setKeyword(e.target.value)
        doSearch(e)
        inputRef?.current?.focus()
    }

    function handleInputKeydown(e: any) {
        if (e.key.toUpperCase() == 'ARROWDOWN') {
            e.preventDefault()
            scrollDown()
        }
        if (e.key.toUpperCase() == 'ARROWUP') {
            e.preventDefault()
            scrollUp()
        }
        if (e.key.toUpperCase().indexOf('ENTER') >= 0) {
            e.preventDefault()
            chooseItem()
            setKeyword('')
            setDropdown(false)
        }
    }

    const SearchInput = useCallback(({ ref, text, onChange }) => {
        ref?.current?.focus()
        return <input
            type="text"
            name="keyword"
            id="keyword"
            key="universal_search"
            autoComplete="off"
            ref={ref}
            className={classNames(
                'focus:ring-primary-light focus:border-primary-light rounded-md bg-transparent',
                'block w-full py-2 pl-12',
                categories.length == 1 ? 'pr-12 ' : 'pr-28',
                'border-gray-300 text-gray-500 placeholder-gray-300 hover:cursor-pointer'
            )}
            onChange={onChange}
            onKeyDown={handleInputKeydown}
            value={text}
            placeholder={search}
        />
    }, [search])

    return (
        <div className='w-full'>
            <div
                className={classNames(
                    'relative rounded-md  flex',
                    className || '',
                    categories.length == 1 ? '' : '',
                    'w-full'
                )}
                onMouseEnter={() => inputRef?.current?.focus()}
            >
                <div className=" search-with-icon  pr-5 flex items-center ">
                    <span className="feather feather-search text-primary-light text-lg pointer-events-none" />
                    <SearchInput ref={inputRef} text={keyword} onChange={handleInputChange} />
                </div>
           
                <div className=" inset-y-0 right-0 flex items-center ">
                    <div
                        className="h-5 border border-custom-dark-cyan-blue mr-5"
                    ></div>
                    {categories.length == 1 ? (
                        <i
                            className={classNames(
                                'px-4 text-2xl cursor-pointer',
                                is_dropped
                                    ? 'feather-chevron-down'
                                    : 'feather-chevron-up'
                            )}
                            onClick={clearResults}
                        />
                    ) : (
                        <>
                            <Menu
                                as="div"
                                className={classNames(
                                    'relative inline-block text-left',
                                    ' h-full',
                                    'text-gray-300 z-50'
                                )}
                            >
                                <div className="flex items-center justify-center text-white h-full aot-primary-btn mr-5 bg-custom-dark-cyan-blue border-custom-dark-cyan-blue brdr-radius-custom">
                                    <Menu.Button className="inline-flex  w-24 justify-between text-sm  rounded-xl bg-opacity-20 hover:bg-opacity-30 focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-opacity-75">
                                        <Translation
                                            content_key={
                                                !!localStorage.getItem(
                                                    SEARCH_TYPE
                                                )
                                                    ? SEARCH_OPTIONS[
                                                        localStorage.getItem(
                                                            SEARCH_TYPE
                                                        ) as any
                                                    ]?.title
                                                    : SEARCH_OPTIONS[
                                                        selectedOption
                                                    ]?.title
                                            }
                                            render_as="span"
                                            className='font-medium'
                                        />
                                        <ChevronDownIcon
                                            className="w-5 h-5 ml-2 -mr-1 "
                                            aria-hidden="true"
                                        />
                                    </Menu.Button>
                                </div>
                                <Transition
                                    as={Fragment}
                                    enter="transition ease-out duration-100"
                                    enterFrom="transform opacity-0 scale-95"
                                    enterTo="transform opacity-100 scale-100"
                                    leave="transition ease-in duration-75"
                                    leaveFrom="transform opacity-100 scale-100"
                                    leaveTo="transform opacity-0 scale-95"
                                >
                                    <Menu.Items
                                        className="absolute right-0 mt-2 origin-top-right bg-gray-100 divide-y divide-gray-100 rounded-md shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none"
                                        style={{
                                            fontSize: '16px',
                                            height: 'auto',
                                            overflowY: 'auto',
                                            overflowX: 'hidden',
                                            width: '100%',
                                        }}
                                    >
                                        <div className="px-1 py-1 ">
                                            {SEARCH_OPTIONS.map(
                                                (option, index: number) => (
                                                    <Menu.Item
                                                        key={option.id}
                                                    >
                                                        {({ active }) => (
                                                            <button
                                                                className={classNames(
                                                                    active
                                                                        ? 'bg-primary-lighter'
                                                                        : '',
                                                                    'group flex rounded-md items-center w-full px-2 py-2 text-sm',
                                                                
                                                                )}
                                                                style={{
                                                                    color: '#26292A',
                                                                }}
                                                                onClick={() =>
                                                                    handleOptionSelect(
                                                                        option.id
                                                                    )
                                                                }
                                                            >
                                                                <Translation
                                                                    content_key={
                                                                        option.title
                                                                    }
                                                                />
                                                            </button>
                                                        )}
                                                    </Menu.Item>
                                                )
                                            )}
                                        </div>
                                    </Menu.Items>
                                </Transition>
                            </Menu>
                        </>
                    )}
                </div>
            </div>
            {!!debouncedSearchTerm.length &&
                searchResults?.content &&
                displaySuggestions && (
                    <div
                        className="z-50 bg-gray-100 border-gray-200 shadow-md rounded-md h-auto absolute w-fit max-w-loose right-auto mt-2"
                        style={{ borderWidth: '1px' }}
                    >
                        {!!searchResults?.content?.length && (
                            <div className='overflow-y-auto max-h-96'>
                                {searchResults?.content?.map(
                                    (result: any, index: number) => (
                                        <div
                                            className=" w-full cursor-pointer hover:bg-primary-lighter"
                                            key={index}
                                            onClick={() =>
                                                onClickHandler(result)
                                            }
                                        >
                                            <div className="flex items-center h-12 py-1">
                                                <div className="px-10">
                                                    <p className="text-black font-semibold">
                                                        {
                                                            result?.user
                                                                ?.firstName
                                                        }
                                                        &nbsp;
                                                        {
                                                            result?.user
                                                                ?.lastName
                                                        }
                                                    </p>
                                                </div>
                                            </div>
                                            <hr />
                                        </div>
                                    )
                                )}
                            </div>
                        )}

                        {!searchResults?.content?.length && (
                            <div className="px-5 flex items-center my-5">
                                <img
                                    src="/images/error-circle.svg"
                                    alt="error"
                                    className="mr-5 cursor-pointer"
                                    onClick={() => {
                                        setKeyword('')
                                        setSearchResults({})
                                    }}
                                />
                                <div>
                                    <p className="text-gray-900 font-semibold">
                                        <Translation content_key="no_search_result" />
                                    </p>
                                    <p className="text-gray-400">
                                        <Translation content_key="no_search_result_sub_label" />
                                    </p>
                                </div>
                            </div>
                        )}
                    </div>
                )}
        </div>
    )
}

export default forwardRef(UniversalSearch)
